import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Form, Row, Table } from 'react-bootstrap';
import {
	useAsyncDebounce,
	useFilters,
	useGlobalFilter,
	usePagination,
	useTable
} from 'react-table';
import { FormSelect } from 'components/elements/form-select/FormSelect';
import GlobalFilter from 'components/elements/advance-table/GlobalFilter';
import Pagination from 'components/elements/advance-table/Pagination';

// import utility file
import AnneescolaireService from '../../services/AnneescolaireService';

// import utility file
import { Avatar } from '../../../elements/bootstrap/Avatar';
import FraisscolariteService from '../../services/FraisscolariteService';
import SalledeclasseService from '../../services/SalledeclasseService';

const FraisscolaritesListItems = () => {
	const [originalFraisscolarites, setOriginalFraisscolarites] = useState([]);
	const [fraisscolarites, setFraisscolarites] = useState([]);
	const [anneescolaire, setAnneescolaire] = useState([]);
	const [salledeclasse, setSalledeclasse] = useState([]);
	const [yearOptions, setYearOptions] = useState([]);

	useEffect(() => {
		Promise.all([
			FraisscolariteService.getAllFraisscolarites(),
			AnneescolaireService.getAllAnneescolaire(),
			SalledeclasseService.getAllSalledeclasse()
		]).then(([FraisscolaritesData, anneescolaireData, salledeclasseData]) => {
			setOriginalFraisscolarites(FraisscolaritesData);
			setFraisscolarites(FraisscolaritesData);
			setAnneescolaire(anneescolaireData);
			setSalledeclasse(salledeclasseData);
		});
	}, []);

	useEffect(() => {
		const fetchYearOptions = async () => {
			try {
				const years = await AnneescolaireService.getYearOptions();
				setYearOptions(years);
			} catch (error) {
				console.error('Error fetching years options', error);
			}
		};

		fetchYearOptions();
	}, []);

	const YEAR_CHOICES = yearOptions;

	const formatValue = (value, columnId) => {
		if (typeof value === 'string') {
			if (columnId === 'tranche' && value) {
				return value.toUpperCase();
			} else if (columnId === 'montant') {
				return (
					value && value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
				);
			} else {
				return value;
			}
		} else if (columnId === 'salledeclasse') {
			return value && value.intitule_classe
				? formatText(value.intitule_classe).toUpperCase()
				: ''; // Return the intitule_classe property
		}
	};

	const groupData = (data) => {
		return data.reduce((acc, curr) => {
			// Créer une clé unique basée sur 'tranche', 'groupe', et 'anneescolaire'
			const key = `${curr.tranche}-${curr.groupe}-${curr.anneescolaire}`;

			if (!acc[key]) {
				// Si la clé n'existe pas encore, initialiser un nouvel objet avec les données actuelles
				// Nous prenons uniquement le premier montant et la première date_limite trouvée.
				acc[key] = {
					...curr,
					montant: curr.montant, // Un seul montant pour chaque groupe
					date_limite: curr.date_limite // Une seule date_limite pour chaque groupe
				};
			} else {
				// Si la clé existe déjà, ne pas additionner les montants ou dates, mais simplement
				// s'assurer que la date_limite et le montant restent inchangés.
				// Assurez-vous que le montant et la date limite ne changent pas
				// acc[key].montant += curr.montant; // Ce n'est pas nécessaire car le montant est le même pour le groupe
			}

			return acc;
		}, {});
	};

	useEffect(() => {
		const groupedData = groupData(originalFraisscolarites);
		setFraisscolarites(Object.values(groupedData));
	}, [originalFraisscolarites]);

	const globalFilterFn = (rows, ids, filterValue) => {
		// Convert the filterValue to lowercase and remove any underscore characters
		const searchTerm = filterValue.toLowerCase().replace(/_/g, ' ');

		// Loop through each row in the table
		return rows.filter((row) => {
			return row.values.tranche
				.toLowerCase()
				.replace(/_/g, ' ')
				.includes(searchTerm);
		});
	};

	const formatText = (value) => {
		// Remplacez les underscores par des espaces et mettez la première lettre en majuscule
		return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
	};

	const GROUPE_MAPPING = {
		pre_mat: 'Pré-Maternelle',
		mat: 'Maternelle',
		pri: 'Primaire',
		class6_cm2: 'Class 6 et CM2'
	};

	const getGroupDisplayName = (groupValue) => {
		return GROUPE_MAPPING[groupValue] || groupValue;
	};

	const getInitials = (fraisscolarite) => {
		const tranche = fraisscolarite.tranche
			? fraisscolarite.tranche.charAt(0).toUpperCase()
			: '';
		return `${tranche} `;
	};

	const getFilterTerm = (event) => {
		let filterTerm = event.target.value;
		let filteredData = originalFraisscolarites;

		if (filterTerm !== '') {
			filteredData = originalFraisscolarites.filter((fraisscolarite) => {
				return fraisscolarite.anneescolaire === Number(filterTerm);
			});
		}

		// Appliquer la fonction groupData après le filtrage des données
		const groupedData = groupData(filteredData);
		setFraisscolarites(Object.values(groupedData));
	};

	const getAnneescolaireById = (anneescolaireId) => {
		const anneeScolaireObj =
			anneescolaire.find((item) => item.id === anneescolaireId) ?? {};

		return anneeScolaireObj
			? anneeScolaireObj.annee_debut + ' - ' + anneeScolaireObj.annee_fin
			: '';
	};

	const getSalledeclasseById = (salledeclasseId) => {
		return salledeclasse.find((item) => item.id === salledeclasseId) ?? {};
	};

	const columns = useMemo(
		() => [
			{ accessor: 'id', Header: 'ID', show: false },
			{
				accessor: 'tranche',
				Header: 'Tranche',
				Cell: ({ value, row }) => {
					const initials = getInitials(row.original);
					return (
						<div className="d-flex align-items-center">
							<Avatar
								type="initial"
								size="md"
								name={initials}
								className="rounded-circle"
								alt={initials}
							/>
							{value ? (
								<Link to={`/finances/scolarite/${row.original.id}`}>
									<h5 className="mb-0 ms-2">{formatText(value, 'tranche')}</h5>
								</Link>
							) : null}
						</div>
					);
				}
			},
			{
				accessor: 'montant',
				Header: 'Montant',
				Cell: ({ value }) => <div>{formatValue(value, 'montant')}</div>
			},
			{
				accessor: 'groupe',
				Header: 'Groupe',
				Cell: ({ value }) => <div>{getGroupDisplayName(value)}</div>
			},
			{
				accessor: 'anneescolaire',
				Header: 'Annee Scolaire',
				Cell: ({ value }) => (
					<div>{value ? formatValue(value, 'anneescolaire') : '-'}</div>
				)
			},
			{
				accessor: 'date_limite',
				Header: 'Date Limite',
				Cell: ({ value }) => <div>{value || '-'}</div>
			}
		],
		[]
	);

	const data = useMemo(() => {
		return fraisscolarites.map((fraisscolarite) => ({
			...fraisscolarite,
			anneescolaire: getAnneescolaireById(fraisscolarite.anneescolaire),
			salledeclasse: getSalledeclasseById(fraisscolarite.salledeclasse)
		}));
	}, [fraisscolarites]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		page,
		nextPage,
		previousPage,
		state,
		gotoPage,
		pageCount,
		prepareRow,
		setGlobalFilter
	} = useTable(
		{
			columns,
			data,
			initialState: {
				pageSize: 10,
				hiddenColumns: columns.map((column) => {
					if (column.show === false) return column.accessor || column.id;
					else return false;
				}),
				sortBy: [
					{ id: 'anneescolaire', desc: true },
					{ id: 'groupe', desc: true },
					{ id: 'tranche', desc: true }
				]
			},
			globalFilter: globalFilterFn
		},
		useFilters,
		useGlobalFilter,
		usePagination
	);

	const { pageIndex, globalFilter } = state;

	return (
		<Fragment>
			<div className="overflow-hidden p-3">
				<Row className="justify-content-md-between mb-4 mb-xl-0 ">
					<Col xxl={3} lg={4} md={6} xs={12}>
						<GlobalFilter
							filter={globalFilter}
							setFilter={useAsyncDebounce(setGlobalFilter, 200)}
							placeholder="Rechercher une scolarite"
						/>
					</Col>
					<Col xxl={3} lg={4} md={6} xs={12}>
						{/* records filtering options */}
						<Form.Control
							as={FormSelect}
							placeholder="Filtrer par année scolaire"
							options={YEAR_CHOICES}
							onChange={getFilterTerm}
						/>
					</Col>
				</Row>
			</div>

			<Table
				hover
				responsive
				{...getTableProps()}
				className="table text-nowrap table-centered"
			>
				<thead className="table-light">
					{headerGroups.map((headerGroup) => (
						<tr {...headerGroup.getHeaderGroupProps()}>
							{headerGroup.headers.map((column) => (
								<th {...column.getHeaderProps()}>{column.render('Header')}</th>
							))}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{page.map((row) => {
						prepareRow(row);
						return (
							<tr {...row.getRowProps()}>
								{row.cells.map((cell) => {
									return (
										<td {...cell.getCellProps()}>{cell.render('Cell')}</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</Table>

			<Pagination
				previousPage={previousPage}
				pageCount={pageCount}
				pageIndex={pageIndex}
				gotoPage={gotoPage}
				nextPage={nextPage}
			/>
		</Fragment>
	);
};

export default FraisscolaritesListItems;
